import { useCallback, useEffect, useRef } from 'react'
import { usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import { useConversationChannel, TypingData, MessagesReadData, EndUserUpdatedData, MessageUpdatedData, MessageDeletedData } from '@/hooks/useConversationChannel'
import { useInboxChannel, EndUserOnlineStatus, AgentPresenceData } from '@/hooks/useInboxChannel'
import { playNotificationSound, isTabHidden } from '@/lib/notificationSound'
import type { ConversationListItem, ConversationDetail, Message, InternalNote } from '@/components/inbox/types'

interface UseInboxWebSocketParams {
  selectedConversationId: number | null
  currentAgentId: number
  setConversations: React.Dispatch<React.SetStateAction<ConversationListItem[]>>
  setSelectedConversation: React.Dispatch<React.SetStateAction<ConversationDetail | null>>
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  setInternalNotes: React.Dispatch<React.SetStateAction<InternalNote[]>>
  setTypingUsers: React.Dispatch<React.SetStateAction<Array<{ user_id: number; user_name: string; user_type: string; avatar_url: string | null }>>>
  setConversationPresence: React.Dispatch<React.SetStateAction<Map<number, Array<{ id: number; name: string; avatar_url: string | null }>>>>
  typingTimeoutRef: React.MutableRefObject<{ [key: number]: number }>
  matchesCurrentFilter: (conv: ConversationListItem) => boolean
  requestSync: (immediate?: boolean) => void
}

export function useInboxWebSocket({
  selectedConversationId,
  currentAgentId,
  setConversations,
  setSelectedConversation,
  setMessages,
  setInternalNotes,
  setTypingUsers,
  setConversationPresence,
  typingTimeoutRef,
  matchesCurrentFilter,
  requestSync,
}: UseInboxWebSocketParams) {
  const { currentAgentTeamIds = [] } = usePage<{ currentAgentTeamIds?: number[] }>().props

  // Marks the open conversation as read on the server when end-user messages
  // arrive while the tab is visible; if the tab is hidden, the read is
  // deferred until the tab becomes visible again.
  const markReadRef = useRef<(() => void) | null>(null)
  const pendingReadRef = useRef(false)

  // Replace the first optimistic item that matches, or just append the real one
  function replaceOptimistic<T extends { _optimistic?: boolean; _sendFailed?: boolean }>(
    items: T[],
    realItem: T,
    isMatch: (optimistic: T) => boolean,
    onReplace?: (optimistic: T) => void,
  ): T[] {
    let replaced = false
    const filtered = items.filter(item => {
      if (!replaced && item._optimistic && !item._sendFailed && isMatch(item)) {
        replaced = true
        onReplace?.(item)
        return false
      }
      return true
    })
    return [...filtered, realItem]
  }

  const handleMessageReceived = useCallback((message: Message) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev
      return replaceOptimistic(
        prev,
        message,
        (m) => m.body === message.body && m.author_type === message.author_type,
        (m) => {
          // Revoke blob preview URLs from optimistic attachments
          m.attachments?.forEach(a => {
            if (a.url?.startsWith('blob:')) URL.revokeObjectURL(a.url)
          })
        },
      )
    })
    if (message.author_type === 'EndUser') {
      if (isTabHidden()) {
        playNotificationSound()
        pendingReadRef.current = true
      } else {
        // The agent is looking at this conversation: keep the server-side
        // read cursor in sync so the unread count doesn't accumulate.
        markReadRef.current?.()
        requestSync()
      }
    }
  }, [requestSync])

  // When the tab becomes visible again with messages received while hidden,
  // mark the open conversation as read and resync.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isTabHidden() && pendingReadRef.current) {
        pendingReadRef.current = false
        markReadRef.current?.()
        requestSync()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [requestSync])

  // Pending reads belong to the previously selected conversation
  useEffect(() => {
    pendingReadRef.current = false
  }, [selectedConversationId])

  const handleNoteReceived = useCallback((note: InternalNote) => {
    setInternalNotes((prev) => {
      if (prev.some((n) => n.id === note.id)) return prev
      return replaceOptimistic(
        prev,
        note,
        (n) => n.body === note.body && n.agent.id === note.agent.id,
        (n) => {
          // Revoke blob preview URLs from optimistic attachments
          n.attachments?.forEach(a => {
            if (a.url?.startsWith('blob:')) URL.revokeObjectURL(a.url)
          })
        },
      )
    })
  }, [])

  const handleTypingReceived = useCallback((typingData: TypingData) => {
    const { user_id, user_name, user_type, avatar_url, is_typing } = typingData
    // Ignore own typing events — the agent already knows they're typing
    if (user_type === 'Agent' && user_id === currentAgentId) return
    if (is_typing) {
      setTypingUsers((prev) => {
        if (prev.some((u) => u.user_id === user_id)) return prev
        return [...prev, { user_id, user_name, user_type, avatar_url }]
      })
      if (typingTimeoutRef.current[user_id]) clearTimeout(typingTimeoutRef.current[user_id])
      typingTimeoutRef.current[user_id] = setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => u.user_id !== user_id))
        delete typingTimeoutRef.current[user_id]
      }, 3000)
    } else {
      setTypingUsers((prev) => prev.filter((u) => u.user_id !== user_id))
      if (typingTimeoutRef.current[user_id]) {
        clearTimeout(typingTimeoutRef.current[user_id])
        delete typingTimeoutRef.current[user_id]
      }
    }
  }, [currentAgentId])

  const handleAgentPresence = useCallback((data: AgentPresenceData) => {
    if (data.agent_id === currentAgentId) return
    setConversationPresence((prev) => {
      const next = new Map(prev)
      const existingAgents = next.get(data.conversation_id) || []
      if (data.agent) {
        if (!existingAgents.some((a) => a.id === data.agent_id)) {
          next.set(data.conversation_id, [...existingAgents, data.agent])
        }
      } else {
        const filtered = existingAgents.filter((a) => a.id !== data.agent_id)
        if (filtered.length > 0) {
          next.set(data.conversation_id, filtered)
        } else {
          next.delete(data.conversation_id)
        }
      }
      return next
    })
  }, [currentAgentId])

  const handleMessagesRead = useCallback((data: MessagesReadData) => {
    setMessages((prev) =>
      prev.map((msg) =>
        data.message_ids.includes(msg.id) ? { ...msg, read_at: data.read_at } : msg
      )
    )
  }, [])

  const handleEndUserUpdated = useCallback((data: EndUserUpdatedData) => {
    setSelectedConversation((prev) => {
      if (!prev) return prev
      return { ...prev, end_user: { ...prev.end_user, ...data } }
    })
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === selectedConversationId
          ? { ...conv, end_user: { ...conv.end_user, ...data } }
          : conv
      )
    )
  }, [selectedConversationId])

  const handleMessageUpdated = useCallback((data: MessageUpdatedData) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === data.id
          ? { ...msg, body: data.body, system_message_type: data.system_message_type, metadata: data.metadata, edited_at: data.edited_at }
          : msg
      )
    )
  }, [])

  const handleMessageDeleted = useCallback((data: MessageDeletedData) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === data.message_id ? { ...msg, deleted_at: new Date().toISOString() } : msg
      )
    )
  }, [])

  const handleWhatsAppSendError = useCallback((data: { message_id: number; error: string }) => {
    toast.error(data.error)
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === data.message_id
          ? { ...msg, whatsapp_status: 'failed' as const, whatsapp_error_message: data.error }
          : msg
      )
    )
  }, [])

  const { notifyTyping, markRead } = useConversationChannel({
    conversationId: selectedConversationId || 0,
    onMessageReceived: handleMessageReceived,
    onNoteReceived: handleNoteReceived,
    onTypingReceived: handleTypingReceived,
    onMessagesRead: handleMessagesRead,
    onEndUserUpdated: handleEndUserUpdated,
    onMessageUpdated: handleMessageUpdated,
    onMessageDeleted: handleMessageDeleted,
    onWhatsAppSendError: handleWhatsAppSendError,
  })

  // Inbox channel: conversation updates + end user online status.
  // The event payload is only used for an optimistic preview update (last
  // message, ordering); unread counts and stats are never computed locally —
  // the debounced sync pulls the authoritative values from the server.
  const handleConversationUpdated = useCallback((updatedConversation: ConversationListItem) => {
    const convId = updatedConversation.id

    setConversations((prev) => {
      const existingIndex = prev.findIndex((c) => c.id === convId)

      if (existingIndex >= 0) {
        // Keep the locally known unread_count; the sync will correct it
        const updatedWithCount = { ...updatedConversation, unread_count: prev[existingIndex].unread_count }

        if (matchesCurrentFilter(updatedWithCount)) {
          const updated = [...prev]
          updated[existingIndex] = updatedWithCount
          return updated.sort((a, b) =>
            new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
          )
        }
        return prev.filter((_, i) => i !== existingIndex)
      }

      if (!matchesCurrentFilter(updatedConversation)) return prev
      // Provisional unread for conversations not yet in the list
      const newUnreadCount = updatedConversation.has_new_end_user_message ? 1 : 0
      return [{ ...updatedConversation, unread_count: newUnreadCount }, ...prev].sort((a, b) =>
        new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      )
    })

    // Pull authoritative unread counts, stats and team counts
    requestSync()

    // Play notification sound for relevant new EndUser messages
    if (
      updatedConversation.id !== selectedConversationId &&
      updatedConversation.has_new_end_user_message
    ) {
      const isAssignedToMe = updatedConversation.assigned_agent?.id === currentAgentId
      const isAssignedToOther = updatedConversation.assigned_agent && updatedConversation.assigned_agent.id !== currentAgentId
      const isInMyTeam = updatedConversation.team_id && currentAgentTeamIds.includes(updatedConversation.team_id)
      const hasNoTeam = !updatedConversation.team_id
      const isRelevant = isAssignedToMe || (!isAssignedToOther && (isInMyTeam || hasNoTeam))
      if (isRelevant) playNotificationSound()
    }
  }, [selectedConversationId, currentAgentId, currentAgentTeamIds, matchesCurrentFilter, requestSync])

  const handleEndUserOnlineStatusChanged = useCallback((data: EndUserOnlineStatus) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.end_user.id === data.end_user_id
          ? { ...conv, end_user: { ...conv.end_user, last_seen_at: data.last_seen_at } }
          : conv
      )
    )
    setSelectedConversation((prev) =>
      prev && prev.end_user.id === data.end_user_id
        ? { ...prev, end_user: { ...prev.end_user, last_seen_at: data.last_seen_at } }
        : prev
    )
  }, [])

  markReadRef.current = markRead

  useInboxChannel({
    onConversationUpdated: handleConversationUpdated,
    onEndUserOnlineStatusChanged: handleEndUserOnlineStatusChanged,
    onAgentPresence: handleAgentPresence,
    onReconnect: () => requestSync(true),
  })

  return { notifyTyping }
}
